/**
 * Flywheel state. Three actors (alumni → candidate → operator → alumni) and
 * the events that flow between them. Components throughout the app emit
 * events when their action lands; the indicator + intro components subscribe.
 *
 * No persistence — events are ephemeral, tied to the session. The point is
 * the visual: when a candidate sends a message, the candidate node pulses
 * and the arc to the operator (signal flows there) lights up briefly.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type FlywheelActor = 'alumni' | 'candidate' | 'operator';

export type FlywheelEventKind =
  | 'SENPAI_LOG_APPENDED' // alumni contributes
  | 'MATCH_CREATED' // candidate searches
  | 'MESSAGE_SENT' // candidate talks (also CONVERSATION_COMPLETED — same node)
  | 'DASHBOARD_REFRESHED'; // operator observes

export interface FlywheelEvent {
  kind: FlywheelEventKind;
  /** Where the event originates — that node pulses */
  actor: FlywheelActor;
  /** Where the resulting signal flows — that arc lights up */
  flowsTo: FlywheelActor;
  /** Free-form note shown alongside the pulse, e.g. "ハルカと対話" */
  note?: string;
  timestamp: number;
}

interface FlywheelState {
  events: FlywheelEvent[];
  /** The most recent active pulse — drives the pulse animation */
  active: FlywheelEvent | null;
  emit: (e: Omit<FlywheelEvent, 'timestamp'>) => void;
}

const FlywheelContext = createContext<FlywheelState | null>(null);

const ACTIVE_DURATION_MS = 1800;
const HISTORY_LIMIT = 20;

export function FlywheelProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<FlywheelEvent[]>([]);
  const [active, setActive] = useState<FlywheelEvent | null>(null);
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const emit = useCallback((e: Omit<FlywheelEvent, 'timestamp'>) => {
    const evt: FlywheelEvent = { ...e, timestamp: Date.now() };
    setEvents((prev) => [...prev.slice(-HISTORY_LIMIT + 1), evt]);
    setActive(evt);

    if (clearTimer.current) clearTimeout(clearTimer.current);
    clearTimer.current = setTimeout(() => setActive(null), ACTIVE_DURATION_MS);
  }, []);

  useEffect(() => () => {
    if (clearTimer.current) clearTimeout(clearTimer.current);
  }, []);

  const value = useMemo<FlywheelState>(() => ({ events, active, emit }), [events, active, emit]);
  return <FlywheelContext.Provider value={value}>{children}</FlywheelContext.Provider>;
}

export function useFlywheel(): FlywheelState {
  const ctx = useContext(FlywheelContext);
  if (!ctx) throw new Error('useFlywheel must be used within FlywheelProvider');
  return ctx;
}

/**
 * Three-actor topology. Used by both the compact indicator and the full
 * intro view to know where to draw nodes and arcs.
 *
 *      alumni          candidate
 *         ╲             ╱
 *          ╲           ╱
 *           ╲         ╱
 *            operator
 *
 * Arcs (always drawn): alumni→candidate, candidate→operator, operator→alumni
 */
export const ACTOR_LABEL: Record<FlywheelActor, string> = {
  alumni: '先輩 (アルムナイ)',
  candidate: '候補者',
  operator: '運営',
};

export const ACTOR_LABEL_SHORT: Record<FlywheelActor, string> = {
  alumni: '先輩',
  candidate: '候補者',
  operator: '運営',
};
